import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { fieldIsEmpty, Fieldset } from '../form';
import { OverlayLoader } from '../loader';
import {
  BaseProspectusFields,
  BaseProspectusFieldsType,
  ContactFields,
  ContactFieldsType,
  FinalTermsDetailsFields,
  FinalTermsDetailsFieldsType,
  Footer,
  Header,
  Information,
  SecurityList,
  SecurityListType,
} from './components';
import { ValidationRuleDto } from './dto';
import { useFinalTermsFormManagement, useGoogleRecaptcha } from './hooks';
import { FinalTermsValidator, SUPPORTED_FORMATS } from './validators';
import _ from 'lodash';
import SubmitFormModal from '../form/modal/submit-form-modal';

library.add(fas);

export type FinalTermType = {
  contact: ContactFieldsType;
  baseProspectus: BaseProspectusFieldsType;
  finalTermsDetails: FinalTermsDetailsFieldsType;
  securities: SecurityListType;
  file: File;
  forceWarning: boolean;
  recaptcha: any;
};

export const InconsistenciesContext = React.createContext<ValidationRuleDto[]>([]);

const API_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY as string;
const RECAPTCHA_ACTION = 'finalTermsCreate';
const API_URL = 'https://www.google.com/recaptcha/api.js?render=';

export const App = () => {
  const { initialValues, handleSubmit, resetForm } = useFinalTermsFormManagement();
  const confirmSubmitModalRef = useRef<SubmitFormModal>(null!);

  const { execute } = useGoogleRecaptcha({
    apiURL: API_URL,
    apiKey: API_KEY,
  });

  const [inconsistencies, setInconsistencies] = useState<ValidationRuleDto[]>([]);

  const preHandleSubmit = async (fields: FinalTermType, helpers: FormikHelpers<FinalTermType>) => {
    try {
      if (execute) {
        const token: string = await execute(API_KEY, { action: RECAPTCHA_ACTION });

        await handleSubmit(
          token,
          fields,
          helpers,
          () => {
            setInconsistencies([]);
            toast.success('Your final term has been submitted successfully to the CSSF!', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          },
          (inconsistencies: ValidationRuleDto[]) => {
            const sortedInconsistencies = _.sortBy(inconsistencies, (v: ValidationRuleDto) => v.message);
            setInconsistencies(sortedInconsistencies);

            toast.error('Some information in your deposit is incorrect', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          },
          () => {
            toast.error('An error occured when submitting the form, please try again later', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          }
        );
      }
    } catch (err) {
      console.error(err);

      toast.error('An error occured when submitting the form, please try again later.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  function hasSecuritiesWithoutConsiderationOffered(fields: FinalTermType) {
    return (
      fields.securities.filter((value) => {
        return (
          fieldIsEmpty(value, 'consideration') ||
          (fieldIsEmpty(value.consideration, 'to') &&
            fieldIsEmpty(value.consideration, 'from') &&
            fieldIsEmpty(value.consideration, 'equal') &&
            fieldIsEmpty(value.consideration, 'pending'))
        );
      }).length > 0
    );
  }

  const confirmSubmit = async (fields: FinalTermType, helpers: FormikHelpers<FinalTermType>) => {
    if (!hasSecuritiesWithoutConsiderationOffered(fields)) {
      await preHandleSubmit(fields, helpers);
    } else {
      try {
        const modal: SubmitFormModal = confirmSubmitModalRef.current;
        await modal.show();
        await preHandleSubmit(fields, helpers);
      } catch (err) {
        console.log(err);
        return Promise.reject('Cancel form');
      }
    }
  };

  return (
    <InconsistenciesContext.Provider value={inconsistencies}>
      <Formik initialValues={initialValues} validationSchema={FinalTermsValidator} onSubmit={confirmSubmit}>
        {(props) => {
          return (
            <>
              <Container className="app">
                <ToastContainer
                  position="top-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                />
                <ToastContainer />
                <Header />
                <Container className="main">
                  <Form>
                    {props.isSubmitting && <OverlayLoader size={'3x'} />}
                    <Row>
                      <Col>
                        <Fieldset legend="Information" className="gray">
                          <Information />
                        </Fieldset>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Fieldset legend="Contact">
                          <ContactFields controlId="contact" />
                        </Fieldset>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Fieldset legend="Base Prospectus Details">
                          <BaseProspectusFields controlId="baseProspectus" />
                        </Fieldset>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Fieldset legend="Final Terms Details">
                          <FinalTermsDetailsFields controlId="finalTermsDetails" />
                        </Fieldset>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Fieldset legend="Securities">
                          <SecurityList controlId="securities" />
                        </Fieldset>
                      </Col>
                    </Row>
                  </Form>
                </Container>
                <Footer
                  controlIdFile="file"
                  resetForm={() => resetForm(props)}
                  onSubmit={props.submitForm}
                  submitting={props.isSubmitting}
                  supportedFormat={SUPPORTED_FORMATS.join(',')}
                  values={props.values}
                />
              </Container>
              <Container className="rgpd">
                <Row>
                  <Col>
                    <p>
                      CSSF processes personal data you provide in the context of this form according to the GDPR (UE 2016/679) as detailed
                      in our Privacy Policy published on the CSSF website :{' '}
                      <a href="https://www.cssf.lu" target="_blank" rel="noopener noreferrer">
                        www.cssf.lu
                      </a>
                      <br />
                      <a href="https://www.cssf.lu/en/terms-of-service-and-privacy-policy/" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a>
                      <br />
                      This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">
                        Privacy Policy
                      </a>{' '}
                      and <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                    </p>
                  </Col>
                </Row>
              </Container>

              <SubmitFormModal
                ref={confirmSubmitModalRef}
                title={'Consideration offered is empty'}
                confirmButtonMessage={'Submit Final Terms'}
                cancelButtonMessage={'Cancel'}
              >
                <>
                  Please note that the field "Consideration offered" is empty. (We remind you that the consideration offered is the total
                  amount of the issue i.e. EUR 500,000,000).
                  <br />
                  <br />
                  This field should only be left empty in case this value is not applicable for the described security.
                  <br />
                  <br />
                  If the consideration offered is mentioned in the Final Terms, this field should be filled in, in order to provide the
                  correct data to ESMA.
                  <br />
                  <br />
                  If this is correct, press "Submit" to transmit the Final Terms to the CSSF otherwise please fill in the correct
                  consideration offered.
                </>
              </SubmitFormModal>
            </>
          );
        }}
      </Formik>
    </InconsistenciesContext.Provider>
  );
};
